/**
 * הצהרת נגישות — TASKS 4.1 · PRD 8.1, 12.4.
 *
 * ⭐ 02.09.2026 — הוחלף בנוסח המאושר של עורך הדין (V2, `Legal Documents/V2`).
 *    ההעברה שמרה על: קישורי `mailto:` / `tel:` כקישורי פעולה, וקישור לעמוד
 *    "צור קשר" הפנימי. ⛔ העיצוב לא הועבר — רק התוכן הסמנטי.
 *
 * ⭐ 03.09.2026 — עו"ד אישר את הנוסח (V2). לפי אישור אריאנה הוסרו:
 *    (א) תיבת האזהרה ("טיוטה"), (ב) סעיף "מצב הבדיקה" (שהיה מהנוסח של עו"ד
 *    ב-V2 ותיאר שבדיקות קורא-מסך/זום/מורשה נגישות טרם בוצעו). ⚠️ הסרת (ב)
 *    היא סטייה מ-V2 שאריאנה בחרה במפורש — הדף מציג כעת עמידה ב-AA בלי הסתייגות,
 *    בעוד שבדיקות ידניות של קורא מסך/זום ובדיקת מורשה נגישות עדיין פתוחות
 *    (TASKS 6.7). פריט פתוח בנפרד מול טל: כותרת "ורכז הנגישות" (אין חובת מינוי).
 *
 * ⭐ רשימת ההתאמות (הכפתור הצף וכו') תואמת את המימוש בפועל
 *    (`AccessibilityWidget.tsx` / `AccessibilityContext.tsx`).
 */
import { Link } from 'react-router-dom';
import {
  COMPANY_LEGAL_NAME,
  EMAIL,
  EMAIL_HREF,
  PHONE_DISPLAY,
  PHONE_TEL_HREF,
} from '../content/businessInfo.js';

const LAST_REVIEWED = '02.09.2026';

export function AccessibilityPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="text-3xl font-bold">הצהרת נגישות</h1>

      <p className="text-muted mt-2 text-sm">
        {COMPANY_LEGAL_NAME} · דוא"ל לפניות:{' '}
        <a href={EMAIL_HREF} className="ltr-inline text-link underline">
          {EMAIL}
        </a>
      </p>

      <div className="mt-8 space-y-6 leading-relaxed">
        <p className="text-muted">
          {COMPANY_LEGAL_NAME} רואה חשיבות עליונה בהנגשת אתר האינטרנט שלה לאנשים עם מוגבלות. אנו
          משקיעים משאבים על מנת להפוך את האתר לנגיש, מתוך אמונה כי לכל אדם מגיעה הזכות לחיות בכבוד,
          בשוויון, בנוחות ובעצמאות.
        </p>

        <section>
          <h2 className="text-xl font-bold">רמת הנגישות באתר</h2>
          <p className="text-muted mt-2">
            האתר תוכנן לעמוד בהוראות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות),
            התשע"ג-2013, ובהתאם להנחיות תקן ישראלי 5568 ("קווים מנחים לנגישות תכנים באינטרנט"),
            המבוססות על הנחיות WCAG 2.1 ברמה AA.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold">התאמות הנגישות שבוצעו באתר</h2>
          <p className="text-muted mt-2">בין ההתאמות שנועדו לאפשר שימוש נגיש באתר:</p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              כפתור נגישות צף הזמין בכל עמודי האתר, המאפשר בין היתר: הגדלת טקסט (100%-175%), מצב
              ניגודיות גבוהה, מצב גווני אפור, הדגשת קישורים, פונט קריא, עצירת אנימציות ותנועה,
              ואיפוס ההגדרות.
            </li>
            <li>מבנה עמוד סמנטי והיררכיית כותרות תקינה.</li>
            <li>ניווט מלא באמצעות מקלדת, עם מסגרת פוקוס (focus) גלויה.</li>
            <li>קישור "דלג לתוכן הראשי" בראש העמוד.</li>
            <li>תמיכה מלאה בכיווניות מימין לשמאל (RTL).</li>
            <li>תיוג ARIA לרכיבים אינטראקטיביים (כגון תצוגת תמונה מוגדלת וסינון קטלוג).</li>
            <li>התחשבות בהעדפת המשתמש לצמצום תנועה (prefers-reduced-motion).</li>
            <li>טקסט חלופי (Alt Text) לתמונות התוכן באתר.</li>
            <li>תכנים שנכתבו בשפה ברורה, קריאה ומובנת.</li>
            <li>תאימות לצפייה ברזולוציות שונות ובדפדפנים הנפוצים (עיצוב רספונסיבי).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold">נגישות פיזית</h2>
          <p className="text-muted mt-2">
            לעסק אין חנות או מקום עסק הפתוח לקהל הרחב; השירות ניתן באמצעות האתר, הטלפון והדוא"ל
            בלבד. משכך, הוראות הנגישות הנוגעות למבנים ולמקום ציבורי אינן רלוונטיות לפעילות זו.
          </p>
        </section>

        <section>
          <h2 id="פנייה-בנושא-נגישות" className="text-xl font-bold">
            פנייה בנושא נגישות ורכז הנגישות
          </h2>
          <p className="text-muted mt-2">
            אם נתקלתם בבעיה או בקושי בנושא נגישות באתר, או אם אתם זקוקים לעזרה, נשמח לקבל מכם משוב.
            נשתדל לטפל בפנייה ולתת מענה ראשוני בתוך זמן סביר, ולא יאוחר מ-14 ימי עבודה.
          </p>
          <p className="text-muted mt-2">ערוצי פנייה בנושא נגישות:</p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              דוא"ל:{' '}
              <a href={EMAIL_HREF} className="ltr-inline text-link underline">
                {EMAIL}
              </a>
            </li>
            <li>
              טלפון:{' '}
              <a href={PHONE_TEL_HREF} className="ltr-inline text-link underline">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              עמוד{' '}
              <Link to="/contact" className="text-link underline">
                צור קשר
              </Link>{' '}
              באתר.
            </li>
          </ul>
        </section>

        <p className="text-muted text-sm">
          © כל הזכויות שמורות ל-{COMPANY_LEGAL_NAME}, 2026. עודכן לאחרונה: {LAST_REVIEWED}.
        </p>
      </div>
    </div>
  );
}
